import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProductionDeclarationMaterialPlacesListItem } from '@/features/production-declaration/components/statement/production-declaration-material-places-list-item';
import type { ProductionPlace } from '@/types/core/production-declaration/production-place';
import type { ProductionDeclarationStatusCode } from '@/types/core/purchase-order/production-declaration-status-code';

const PRODUCTION_PLACE_SKELETON_ROW_KEYS = [
  'production-place-skeleton-1',
  'production-place-skeleton-2',
  'production-place-skeleton-3',
] as const;

const PRODUCTION_PLACE_SKELETON_CELL_KEYS = [
  'location',
  'forest-type',
] as const;
const PRODUCTION_PLACE_SKELETON_ACTION_KEYS = ['edit', 'delete'] as const;

type ProductionDeclarationMaterialPlacesListProps = {
  commodityOriginId: string;
  isLoading: boolean;
  materialId: string;
  productionDeclarationId: string;
  productionPlaces: ProductionPlace[];
  statusCode: ProductionDeclarationStatusCode;
  token?: string;
};

function ProductionDeclarationMaterialPlacesListHeader() {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  return (
    <TableHeader>
      <TableRow className='border-declaration-muted hover:bg-transparent'>
        <TableHead className='w-[26rem] px-0 pb-3 font-medium text-base text-declaration-muted-foreground'>
          {t(
            'materialCommodityOrigins.cards.productionPlace.columns.locationName',
          )}
        </TableHead>
        <TableHead className='w-[16rem] px-0 pb-3 font-medium text-base text-declaration-muted-foreground'>
          {t(
            'materialCommodityOrigins.cards.productionPlace.columns.forestType',
          )}
        </TableHead>
        <TableHead className='w-[10rem] px-0 pb-3 text-right font-medium text-base text-declaration-muted-foreground'>
          {t('materialCommodityOrigins.cards.productionPlace.columns.actions')}
        </TableHead>
      </TableRow>
    </TableHeader>
  );
}

function ProductionDeclarationMaterialPlacesListLoadingState() {
  return (
    <Table className='min-w-[48rem] table-fixed'>
      <ProductionDeclarationMaterialPlacesListHeader />
      <TableBody>
        {PRODUCTION_PLACE_SKELETON_ROW_KEYS.map((rowKey) => (
          <TableRow
            className='border-declaration-border-light hover:bg-transparent'
            key={rowKey}
          >
            {PRODUCTION_PLACE_SKELETON_CELL_KEYS.map((cellKey) => (
              <TableCell className='px-0 py-5' key={cellKey}>
                <Skeleton
                  className={`h-8 rounded-full bg-declaration-skeleton ${
                    cellKey === 'location' ? 'w-48' : 'w-28'
                  }`}
                />
              </TableCell>
            ))}
            <TableCell className='px-0 py-5'>
              <div className='flex justify-end gap-3'>
                {PRODUCTION_PLACE_SKELETON_ACTION_KEYS.map((actionKey) => (
                  <Skeleton
                    className='size-7 rounded-md bg-declaration-skeleton'
                    key={actionKey}
                  />
                ))}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ProductionDeclarationMaterialPlacesList({
  commodityOriginId,
  isLoading,
  materialId,
  productionDeclarationId,
  productionPlaces,
  statusCode,
  token,
}: ProductionDeclarationMaterialPlacesListProps) {
  if (isLoading) {
    return <ProductionDeclarationMaterialPlacesListLoadingState />;
  }

  if (productionPlaces.length === 0) {
    return null;
  }

  return (
    <Table className='min-w-[48rem] table-fixed'>
      <ProductionDeclarationMaterialPlacesListHeader />
      <TableBody>
        {productionPlaces.map((productionPlace) => (
          <ProductionDeclarationMaterialPlacesListItem
            commodityOriginId={commodityOriginId}
            key={productionPlace.id}
            materialId={materialId}
            productionDeclarationId={productionDeclarationId}
            productionPlace={productionPlace}
            statusCode={statusCode}
            token={token}
          />
        ))}
      </TableBody>
    </Table>
  );
}
